/**
 * Bedrock Embeddings Client
 *
 * Builds the OpenAI-shaped embeddings client used by retrieval, upload, chat
 * search, and scratchpad flows. Always resolves to AWS Bedrock (Cohere Embed
 * v4), never OpenAI or Azure.
 *
 * Primary responsibilities:
 * - Expose a single `embeddings.create(...)` entry point over Bedrock
 *
 * Dependencies:
 * - lib/bedrock/embeddings
 */

import { bedrockEmbeddings, hasBedrockEmbeddingsAuth } from '@/lib/bedrock/embeddings'
import { Tables } from '@/supabase/types'

export const OPENAI_EMBEDDING_MODEL = 'text-embedding-3-small'

export type OpenAIEmbeddingProfile = Pick<Tables<'profiles'>, 'user_id'> & {
  email?: string | null
}

// `profile` is accepted (but unused) so existing call sites that resolve a
// profile for other reasons don't need to change their call shape.
export function canUseOpenAIEmbeddings(_profile?: OpenAIEmbeddingProfile | null) {
  return hasBedrockEmbeddingsAuth()
}

// Duck-typed adapter that exposes `client.embeddings.create(...)` matching the
// OpenAI SDK — so every existing call site (`client.embeddings.create({ input,
// model })`) works unchanged.
type EmbeddingsClient = { embeddings: { create: typeof bedrockEmbeddings.create } }

export function createOpenAIEmbeddingsClient(
  _profile?: OpenAIEmbeddingProfile | null
): EmbeddingsClient {
  return { embeddings: bedrockEmbeddings }
}
